<template>
  <button
    :class="['na-day-item', { active: isActive, forecast: isForecast, 'na-day-item-lut': isLut }]"
    @click="$emit('select', index)"
  >
    <div class="na-day-label">
      <template v-if="isForecast">
        <span class="na-day-text">previsão</span>
      </template>
      <template v-else>
        <span class="na-day-text">{{ dayAbbr }}. {{ dayNumber }}</span>
      </template>
    </div>

    <!-- LUT e Fotobio: mostram apenas a data, sem valor atual e sem mín/máx
         (no Fotobio são vários tanques; um mín/máx único confunde) -->
    <template v-if="!isLut">
      <!-- Ritter: volume medido no dia -->
      <div class="na-day-rain" v-if="stationType === 'ritter'">
        <span class="na-day-value">{{ volumeDiaDisplay }}</span>
        <span class="na-day-unit">L</span>
      </div>
      <!-- Ambiente: temperatura (umidade é opcional nesse sensor) -->
      <div class="na-day-rain" v-else-if="stationType === 'ambiente'">
        <span class="na-day-value">{{ ambienteTempDisplay }}°c</span>
      </div>
      <div class="na-day-rain" v-else-if="isSoilType">
        <span class="na-day-value">{{ soilHumidityDisplay }}%</span>
      </div>
      <!-- Sondas em dia encerrado: só mín/máx. A "última leitura" de um dia que já
           acabou nao diz nada sobre o nivel agora, enquanto os extremos resumem o
           que aconteceu. Mesma regra do aside de dispositivo externo. -->
      <div class="na-day-minmax" v-else-if="hasDepth && !isCurrentDay">
        <span class="na-day-minmax-item">
          <span class="na-day-minmax-cap">max</span>
          <span class="na-day-minmax-val">{{ depthMaxDisplay }}</span>
        </span>
        <span class="na-day-minmax-item">
          <span class="na-day-minmax-cap">min</span>
          <span class="na-day-minmax-val">{{ depthMinDisplay }}</span>
        </span>
        <span class="na-day-minmax-unit">m</span>
      </div>
      <div class="na-day-rain" v-else-if="hasDepth">
        <span class="na-day-value">{{ depthDisplay }}</span>
        <span class="na-day-unit">m</span>
      </div>
      <div class="na-day-rain" v-else>
        <span class="na-day-value">{{ rainfallDisplay }}</span>
        <span class="na-day-unit">mm</span>
      </div>

      <div class="na-day-temp" v-if="isSoilType && hasTemperature">
        <span class="na-day-temp-range">{{ tempMinDisplay }}°/{{ tempMaxDisplay }}°</span>
      </div>
      <div class="na-day-temp" v-else-if="hasDepth && isCurrentDay">
        <span class="na-day-temp-range">{{ depthMinDisplay }}m/{{ depthMaxDisplay }}m</span>
      </div>
      <div class="na-day-temp" v-else-if="hasTemperature">
        <span class="na-day-temp-range">{{ tempMinDisplay }}°/{{ tempMaxDisplay }}°</span>
      </div>
    </template>
  </button>
</template>

<script>
import { SONDA_LINKNAMES } from "../../mixin/sondas";

const WEEK_ABBR = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default {
  name: "NewAsideDayItem",
  props: {
    dayData: { type: Object, required: true },
    index: { type: Number, required: true },
    isActive: { type: Boolean, default: false },
    stationType: { type: String, default: "estacao" },
    isForecast: { type: Boolean, default: false },
    // Dia mais recente da lista. So nele existe "leitura atual"; dias encerrados
    // mostram apenas mín/máx.
    isCurrentDay: { type: Boolean, default: false },
  },
  emits: ["select"],
  computed: {
    dateObj() {
      const raw = this.stationType === "clima"
        ? this.dayData.cidade_clima?.data
        : this.dayData.date;
      return raw ? new Date(raw) : new Date();
    },
    dayAbbr() {
      return WEEK_ABBR[this.dateObj.getDay()];
    },
    dayNumber() {
      return this.dateObj.getDate();
    },
    resume() {
      return this.dayData.resume || {};
    },
    cidadeClima() {
      return this.dayData.cidade_clima || {};
    },
    rainfallDisplay() {
      if (this.stationType === "clima") {
        const val = this.cidadeClima.precipitacao_total;
        return val != null ? val.toFixed(2) : 0;
      }
      const val = this.resume.rainfallTotal;
      return val != null ? val.toFixed(2) : 0;
    },
    isSoilType() {
      return this.stationType === 'solo' || this.stationType === 'folhar' || this.stationType === 'ambiente';
    },
    isLut() {
      return this.stationType === 'lut' || this.stationType === 'fotobio';
    },
    hasTemperature() {
      if (this.stationType === "clima") {
        return this.cidadeClima.temperatura_max != null;
      }
      if (this.isSoilType) {
        return this.resume.temperatureMax != null;
      }
      return this.resume.temperatureMax != null && this.stationType === "estacao";
    },
    // Com mais de um sensor no dispositivo, a aba segue o primeiro card do aside: mesmo
    // agrupamento por sourceId e, no solo, mesma ordenação por profundidade (soilSensors).
    soilLastReading() {
      const all = this.dayData.hours || [];
      if (!all.length) return null;
      const grouped = {};
      all.forEach((h) => {
        const key = h.sourceId || 'default';
        if (!grouped[key]) grouped[key] = { depth: h.depth, hours: [] };
        grouped[key].hours.push(h);
      });
      let sensores = Object.values(grouped);
      if (this.stationType === 'solo') {
        sensores = sensores.sort((a, b) => (a.depth || 0) - (b.depth || 0));
      }
      const hours = sensores[0].hours;
      return hours[hours.length - 1];
    },
    soilHumidityDisplay() {
      const last = this.soilLastReading;
      return last?.humidity != null ? last.humidity.toFixed(2) : '--';
    },
    ambienteTempDisplay() {
      const last = this.soilLastReading;
      return last?.temperature != null ? last.temperature.toFixed(2) : '--';
    },
    hasDepth() {
      return SONDA_LINKNAMES.includes(this.stationType) && this.resume.depthMax != null;
    },
    // Solo/folhar/ambiente: mín/máx do mesmo sensor do valor principal (o resumo geral
    // mistura todos os sensores do dispositivo e não batia com o card do aside).
    soilTempResumo() {
      if (!this.isSoilType) return null;
      const last = this.soilLastReading;
      if (!last) return null;
      const sourceId = last.sourceId || 'default';
      const s = (this.resume.sensores || []).find((x) => x.sourceId === sourceId);
      return s ? s.campos.find((c) => c.field === 'temperature') || null : null;
    },
    tempMinDisplay() {
      if (this.stationType === "clima") {
        const val = this.cidadeClima.temperatura_min;
        return val != null ? Math.round(val) : "0";
      }
      const val = this.soilTempResumo ? this.soilTempResumo.min : this.resume.temperatureMin;
      return val != null ? val.toFixed(1) : "0";
    },
    tempMaxDisplay() {
      if (this.stationType === "clima") {
        const val = this.cidadeClima.temperatura_max;
        return val != null ? Math.round(val) : "0";
      }
      const val = this.soilTempResumo ? this.soilTempResumo.max : this.resume.temperatureMax;
      return val != null ? val.toFixed(1) : "0";
    },
    depthDisplay() {
      const hours = this.dayData.hours || [];
      if (!hours.length) return '--';
      const last = hours[hours.length - 1];
      const val = last.depthCalc != null ? last.depthCalc : last.depth;
      return val != null ? val.toFixed(2) : '--';
    },
    volumeDiaDisplay() {
      const val = this.resume.volumeDia;
      if (val == null) return "--";
      return val >= 1000 ? Math.round(val).toLocaleString("pt-BR") : val.toFixed(1);
    },
    depthMinDisplay() {
      const val = this.resume.depthMin;
      return val != null ? val.toFixed(2) : "0";
    },
    depthMaxDisplay() {
      const val = this.resume.depthMax;
      return val != null ? val.toFixed(2) : "0";
    },
  },
};
</script>

<style scoped>
.na-day-item {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  border-radius: 8px;
  background: #F2F4F1;
  text-align: center;
  padding: 6px 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.na-day-item:hover {
  background: #e8ebe6;
}

/* LUT mostra apenas a data, então damos mais altura ao card */
.na-day-item-lut {
  min-height: 56px;
  justify-content: center;
}

.na-day-item.active {
  background-color: #FFFFFF;
  border: 1px solid #C3D9AD;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
  position: relative;
  z-index: 3;
  margin-bottom: -1px;
}

.na-day-item.forecast {
  background-color: #F2F4F1;
}

.na-day-text {
  font-size: 11px;
  font-weight: 600;
  color: #333;
}

.na-day-rain {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
}

.na-day-value {
  font-size: 16px;
  font-weight: 700;
  color: #006035;
}

.na-day-unit {
  font-size: 10px;
  color: #666;
}

.na-day-temp-range {
  font-size: 10px;
  color: #666;
}

/* Abas de dias encerrados das sondas: máx/mín empilhados, mesmo visual do aside
   de dispositivo externo. */
.na-day-minmax {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  line-height: 1.1;
  padding: 1px 0 0;
}

.na-day-minmax-item {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.na-day-minmax-cap {
  font-size: 8px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: .2px;
}

.na-day-minmax-val {
  font-size: 12px;
  font-weight: 700;
  color: #006035;
  font-variant-numeric: tabular-nums;
}

.na-day-minmax-unit {
  font-size: 10px;
  color: #666;
}
</style>
